import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export type IntensityMetrics = {
  emissionsPerRevenue: number | null;
  emissionsPerEmployee: number | null;
  energyIntensity: number;
};

export type CarbonPerformance = {
  totalEmissionsTco2e: number;
  scope1Tco2e: number;
  scope2Tco2e: number;
  scope3Tco2e: number;
  intensityMetrics: IntensityMetrics;
  reportingPeriod: string;
};

export type ESGCard = {
  completeness: number;
  metricsProvided: number;
  totalMetrics: number;
};

export type ESGStatus = {
  environmental: ESGCard;
  social: ESGCard;
  governance: ESGCard;
};

export type TrustIndicators = {
  completenessPercent: number;
  vsmeLevel: string;
  evidenceCount: number;
  dataQualityRating: number;
  boundaryStatement: string;
};

export type Hotspot = {
  source: string;
  tco2e: number;
  percentage: number;
};

export type Recommendation = {
  priority: "high" | "medium" | "low";
  title: string;
  description: string;
};

export type ShareLinkInfo = {
  token: string;
  viewCount: number;
  expiresAt: string | null;
  createdAt: string;
};

export type ReportDashboardData = {
  assessment: {
    id: string;
    organizationName: string;
    reportingYear: number;
    currency: string;
    sector: string;
    locked: boolean;
  };
  carbonPerformance: CarbonPerformance;
  esgStatus: ESGStatus;
  trustIndicators: TrustIndicators;
  hotspots: Hotspot[];
  recommendations: Recommendation[];
  shareLink: ShareLinkInfo | null;
};

export type GenerateShareLinkResponse = {
  token: string;
  shareUrl: string;
  expiresAt: string | null;
  createdAt: string;
};

/**
 * Hook to fetch the current year's assessment ID
 */
export function useCurrentAssessmentId() {
  return useQuery<{ assessmentId: string }>({
    queryKey: ["current-assessment"],
    queryFn: async () => {
      const response = await fetch("/api/reports/current", {
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch current assessment");
      }

      return response.json();
    },
    retry: false,
    staleTime: 1000 * 30, // 30 seconds - short enough to detect changes
    refetchOnWindowFocus: false, // Prevent multiple calls on window focus
  });
}

/**
 * Hook to fetch comprehensive report dashboard data
 */
export function useReportDashboard(assessmentId: string | undefined) {
  return useQuery<ReportDashboardData>({
    queryKey: ["report-dashboard", assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error("Assessment ID is required");
      }

      const response = await fetch(`/api/reports/dashboard/${assessmentId}`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch dashboard data");
      }

      return response.json();
    },
    enabled: !!assessmentId,
    staleTime: 0, // Always fetch fresh data to reflect assessment changes
    refetchOnWindowFocus: false, // Prevent refetch on every window focus
    refetchOnMount: true, // Refetch when component mounts to get latest data
    retry: (failureCount, error: any) => {
      // Don't retry on known errors
      if (error.message?.includes("permission") || 
          error.message?.includes("must be locked") ||
          error.message?.includes("not found")) {
        return false;
      }
      return failureCount < 1; // Only retry once
    },
  });
}

/**
 * Hook to generate a share link for the report
 */
export function useGenerateShareLink() {
  const queryClient = useQueryClient();

  return useMutation<GenerateShareLinkResponse, Error, { 
    assessmentId: string; 
    expiresInDays?: number 
  }>({
    mutationFn: async ({ assessmentId, expiresInDays }) => {
      const response = await fetch(`/api/reports/${assessmentId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expiresInDays }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate share link");
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate the dashboard query to refetch with new share link
      queryClient.invalidateQueries({ 
        queryKey: ["report-dashboard", variables.assessmentId] 
      });
    },
  });
}

/**
 * Hook to revoke a share link
 */
export function useRevokeShareLink() {
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean; message: string }, Error, { 
    token: string;
    assessmentId: string;
  }>({
    mutationFn: async ({ token }) => {
      const response = await fetch(`/api/reports/share/${token}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to revoke share link");
      }

      return response.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate the dashboard query to refetch without share link
      queryClient.invalidateQueries({ 
        queryKey: ["report-dashboard", variables.assessmentId] 
      });
    },
  });
}

/**
 * Hook to generate AI-powered text report (existing functionality)
 */
export function useGenerateTextReport() {
  return useMutation<{ success: boolean; report: string; inputData: any }, Error, { assessmentId: string; locale: string }>({
    mutationFn: async ({ assessmentId, locale }) => {
      const response = await fetch(`/api/assessments/${assessmentId}/generate-text-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to generate text report");
      }

      return response.json();
    },
  });
}
